import { useMutation, useSubscription } from "@apollo/client/react";
import { Button } from "common/buttons";
import Popup from "common/components/Popup";
import { Examination } from "domain/simulation/Examination";
import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import styles from "./Chat.module.css";
import { CHAT_SUBSCRIPTION, SEND_MESSAGE_MUTATION } from "./utils";

type Message = {
  content: string;
  role: string;
};

type CaseProps = {
  caseData: SimulationCaseById;
  overrideNextCase?: SimulationCaseList_simulationCaseList[];
  setShowChat: Dispatch<React.SetStateAction<boolean>>;
};

function scrollToBottom(ref: React.RefObject<HTMLDivElement>) {
  if (ref.current) {
    ref.current.scrollIntoView({ behavior: "smooth" });
  }
}

export const Chat: React.FC<PropsWithChildren<CaseProps>> = ({
  caseData,
  overrideNextCase,
  setShowChat,
}) => {
  const greetingMessage = {
    content: "Hei!",
    role: "assistant",
  };

  const patientMessage = {
    content: caseData.simulationCaseById,
    role: "system",
  };

  const [accumulatedData, setAccumulatedData] = useState<Message[]>([
    greetingMessage,
  ]);

  const [messageText, setMessageText] = useState("");

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messageType, setMessageType] = useState("Chat");

  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  const [isWaitingForAssistant, setIsWaitingForAssistant] = useState(false);

  const { data, error, loading } = useSubscription(CHAT_SUBSCRIPTION, {
    onData({ data: subscriptionData }) {
      const message: Message = subscriptionData?.data?.chatSubscription;
      if (message) {
        setAccumulatedData((prev) => [
          ...prev,
          {
            content: message.content,
            role: "assistant",
          },
        ]);
        setIsWaitingForAssistant(false);
      }
    },

    onError: (err) => {
      console.error("Subscription error:", err);
    },
    shouldResubscribe: true,
  });

  const [sendMessage] = useMutation(SEND_MESSAGE_MUTATION, {
    onCompleted(data) {},
    onError(err) {},
  });

  useEffect(() => {
    if (!caseData.simulationCaseById) {
      return (
        <LoadingLayout>
          <NoDataError />
        </LoadingLayout>
      );
    }
  }, [caseData]);

  useEffect(() => {
    scrollToBottom(messagesEndRef);
  }, [accumulatedData]);

  const handleSendMessage = async () => {
    if (messageText.trim()) {
      try {
        const newMessage = { content: messageText, role: "user" };
        const updatedData = [...accumulatedData, newMessage];

        setAccumulatedData(updatedData);
        setMessageText("");
        setIsWaitingForAssistant(true);

        let input = updatedData.slice(-3);
        input.unshift(patientMessage);

        await sendMessage({
          variables: {
            input: {
              content: input,
              type: messageType,
            },
          },
        });
      } catch (error) {
        console.error("Error sending message:", error);
        setIsWaitingForAssistant(false);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key == "Enter") {
      handleSendMessage();
      event.preventDefault();
    }
  };

  return (
    <>
      <div className={styles.chatContainer}>
        <div className={styles.toolbar}>
          <Popup
            trigger="click"
            content={
              <div className={styles.scrollablePopup}>
                <Examination data={caseData} displayReferral={false} />
              </div>
            }
          >
            <div className={styles.popup}>
              <Button kind="secondary">Vis undersøkelser</Button>
            </div>
          </Popup>
          {/*
          <Button
            kind="secondary"
            onClick={() => {
              setMessageType((prev) => (prev === "Chat" ? "Feedback" : "Chat"));
            }}
          >
            {messageType === "Chat" ? "Sett diagnose" : "Snakk med pasient"}
          </Button>
          */}
        </div>
        <div className={styles.messagesList}>
          {accumulatedData.map((message, index) => (
            <div
              key={index}
              className={`${
                message.role === "user"
                  ? styles.userMessage
                  : styles.patientMessage
              }`}
              ref={messagesEndRef}
            >
              <span>
                <strong>{message.role === "user" ? "Deg" : "Pasient"}: </strong>
                {message.content}
              </span>
            </div>
          ))}
          {isWaitingForAssistant && (
            <div className={styles.patientMessage}>
              <span>
                <strong>Pasient: </strong>
                <span className={styles.typingDots}>
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </span>
              </span>
            </div>
          )}
        </div>

        <div className={styles.inputContainer}>
          <input
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Chat med pasient her..."
            className={styles.messageInput}
          />
          <button onClick={handleSendMessage} className={styles.sendButton}>
            Send
          </button>
        </div>
      </div>
      <div className={styles.cancelOrSubmitContainer}>
        <Button
          onClick={() => {
            setShowChat(false);
          }}
        >
          Tilbake til case
        </Button>
      </div>
      {isSubmitClicked && (
        <div className={styles.tryAgain}>
          {overrideNextCase && overrideNextCase.length > 0 ? (
            <Button
              onClick={() => {
                navigate(
                  urls.simulationCase(overrideNextCase.shift()?.id ?? ""),
                  {
                    state: { cases: overrideNextCase },
                  },
                );
                setShowChat(false);
              }}
            >
              Gå til neste pasient
            </Button>
          ) : (
            nextCase?.id && (
              <Link to={urls.simulationCase(nextCase.id)}>
                <Button>Gå til neste pasient</Button>
              </Link>
            )
          )}
        </div>
      )}
    </>
  );
};

export default Chat;
